import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ArrowPathIcon, ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { fadeIn, slideUp } from '../../lib/motion';
import { useCourse } from '../../hooks/learner/useCourse';
import { useAttempt } from '../../hooks/learner/useAttempt';
import { useCourseRoutes } from '../../hooks/learner/useCourseRoutes';
import { useModuleTimer } from '../../hooks/learner/useModuleTimer';
import { getEnrolmentAvailability } from '../../utils/enrolmentAvailability';
import {
  getEvalViewEntry,
  getDefaultEvalAnswer,
  QuizResultView,
} from '../../components/modules/views/registry';
import LearnerModuleHeader from '../../components/learner/LearnerModuleHeader';
import ModuleViewContainer from '../../components/modules/views/ModuleViewContainer';
import type { components } from '../../types/learnerApi';
import type { EvalAnswer } from '../../components/modules/views/registry';

type SanitizedEvaluationItem = components['schemas']['SanitizedEvaluationItem'];
type EvaluationModule = components['schemas']['EvaluationModule'];

function errorCode(err: unknown): string | null {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? null;
}

const DEBOUNCE_MS = 400;

// Per-type eyebrow label shown above the question text in the card.
function questionTypeLabel(type: string): string {
  switch (type) {
    case 'single_choice':
      return 'ONE CORRECT ANSWER';
    case 'multiple_choice':
      return 'MULTIPLE CORRECT ANSWERS';
    case 'boolean_true_false':
      return 'TRUE OR FALSE';
    case 'short_answer_text':
      return 'SHORT ANSWER';
    case 'completion_fill_blank':
      return 'FILL IN THE BLANK';
    case 'free_text_essay':
      return 'ESSAY';
    case 'ordering_sequence':
      return 'PUT IN ORDER';
    case 'tuple_matching':
      return 'MATCH THE PAIRS';
    case 'numeric_input':
      return 'NUMERIC ANSWER';
    case 'code_submission':
      return 'CODE SUBMISSION';
    default:
      return 'QUESTION';
  }
}

interface PrevPillProps {
  onClick: () => void;
  disabled?: boolean;
}
const PrevPill: React.FC<PrevPillProps> = ({ onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center justify-center gap-2 min-w-[120px] h-9 px-3 rounded-full border border-secondary text-secondary text-sm font-bold hover:bg-secondary/10 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-40 disabled:cursor-not-allowed"
  >
    <ArrowLeftIcon className="w-4 h-4" />
    Previous
  </button>
);

interface NextPillProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  label: string;
  finish?: boolean;
}
const NextPill: React.FC<NextPillProps> = ({ onClick, disabled, loading, label, finish }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    aria-busy={loading}
    className={`inline-flex items-center justify-center gap-2 min-w-[151px] h-9 px-4 rounded-full text-sm font-bold shrink-0 active:scale-[0.97] transition-[background-color,transform,opacity] duration-150 disabled:cursor-not-allowed disabled:opacity-60 hover:brightness-110 shadow-[0_2px_8px_rgba(105,72,170,0.25)] ${ finish ? 'bg-gradient-to-t from-success-600 to-success-500' : 'bg-gradient-to-t from-primary-700 to-primary-500' }`}
  >
    {loading ? 'Saving…' : (
      <>
        {finish ? <CheckCircleIcon className="w-4 h-4" /> : null}
        {label}
        {!finish ? <ArrowRightIcon className="w-4 h-4" /> : null}
      </>
    )}
  </button>
);

const EvaluationPlayerPage: React.FC = () => {
  // Learner routes use `:assignmentId`, admin preview routes use `:courseId`.
  const { assignmentId, sectionId = '', moduleId = '' } = useParams<{
    assignmentId?: string;
    sectionId: string;
    moduleId: string;
  }>();
  const id = assignmentId ?? '';
  const navigate = useNavigate();
  const routes = useCourseRoutes(id);

  const { data: course } = useCourse(id);
  const { currentAttempt, start, patchItem, finalize } = useAttempt(id, moduleId);

  // Record time spent on this evaluation, flushed on navigation / tab-close.
  useModuleTimer(id, moduleId);

  // Date-window gate: bounce deep-links back to the detail page when the
  // enrolment hasn't opened yet or has already ended.
  useEffect(() => {
    if (!course) return;
    const availability = getEnrolmentAvailability(course.assignment, {
      completed: course.progress?.bucket === 'completed',
    });
    if (availability.kind !== 'available') {
      navigate(routes.base, { replace: true });
    }
  }, [course, navigate, routes.base]);

  // Locate the evaluation module + its parent section in the course.
  const { evalModule, sectionTitle } = useMemo(() => {
    for (const s of course?.sections ?? []) {
      for (const m of s.modules ?? []) {
        if (m.id === moduleId) {
          return {
            evalModule: m as EvaluationModule,
            sectionTitle: s.title || 'Section',
          };
        }
      }
    }
    return { evalModule: undefined, sectionTitle: 'Section' };
  }, [course, moduleId]);

  // Compute the next module so the result screen can advance into the flow.
  const flat = (course?.sections ?? [])
    .slice()
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .flatMap((s) =>
      (s.modules ?? [])
        .slice()
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        .map((m) => ({ module: m, sectionId: s.id ?? '' }))
    );
  const currentModuleIdx = flat.findIndex((entry) => entry.module.id === moduleId);
  const nextEntry =
    currentModuleIdx >= 0 && currentModuleIdx < flat.length - 1 ? flat[currentModuleIdx + 1] : null;
  const nextModule = nextEntry?.module ?? null;
  const goNext = useCallback(() => {
    // Awaiting-review (status `submitted`, not yet `graded`) on the LAST
    // module: the course's `progress.bucket` can't be `completed` yet, so
    // navigating to `/learn/courses/:id/done` would just hit that page's
    // defensive guard and bounce straight back here ("nothing happens"
    // visually). Send the learner to the course detail page instead so
    // they can see their pending-review state.
    const status = currentAttempt.data?.response?.status;
    const isAwaitingReview = status === 'submitted';
    if (nextEntry?.module.id && nextEntry.sectionId) {
      navigate(
        `${routes.base}/sections/${nextEntry.sectionId}/modules/${nextEntry.module.id}`
      );
    } else if (isAwaitingReview) {
      navigate(routes.base);
    } else {
      navigate(`${routes.base}/done`);
    }
  }, [nextEntry, navigate, currentAttempt.data, routes.base]);

  const items = evalModule?.details?.items ?? [];
  const sortedItems: SanitizedEvaluationItem[] = useMemo(
    () => [...items].sort((a, b) => a.order_index - b.order_index),
    [items]
  );
  const passingGrade = evalModule?.details?.passing_grade_percent ?? null;

  // Local draft answers keyed by item id
  const [answers, setAnswers] = useState<Record<string, EvalAnswer>>({});
  const [pendingSave, setPendingSave] = useState<Set<string>>(new Set());
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetaking, setIsRetaking] = useState(false);
  const [questionIdx, setQuestionIdx] = useState(0);

  // Auto-start attempt if none exists
  useEffect(() => {
    if (!currentAttempt.isLoading && !currentAttempt.data) {
      const code = errorCode(currentAttempt.error);
      if (code !== 'MAX_ATTEMPTS_REACHED') {
        start.mutate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAttempt.isLoading]);

  // Seed draft answers from persisted attempt on mount
  useEffect(() => {
    if (currentAttempt.data?.response?.items) {
      const seeded: Record<string, EvalAnswer> = {};
      for (const [itemId, record] of Object.entries(currentAttempt.data.response.items)) {
        if (record.answer) {
          seeded[itemId] = record.answer as EvalAnswer;
        }
      }
      if (Object.keys(seeded).length > 0) {
        setAnswers((prev) => ({ ...seeded, ...prev }));
      }
    }
  }, [currentAttempt.data]);

  const scheduleAutoSave = useCallback(
    (itemId: string, answer: EvalAnswer) => {
      const existing = debounceTimers.current.get(itemId);
      if (existing) clearTimeout(existing);
      setPendingSave((s) => new Set(s).add(itemId));
      const timer = setTimeout(async () => {
        try {
          await patchItem.mutateAsync({ item_id: itemId, answer });
        } catch {
          // best-effort auto-save
        } finally {
          setPendingSave((s) => {
            const next = new Set(s);
            next.delete(itemId);
            return next;
          });
          debounceTimers.current.delete(itemId);
        }
      }, DEBOUNCE_MS);
      debounceTimers.current.set(itemId, timer);
    },
    [patchItem]
  );

  const handleAnswerChange = useCallback(
    (item: SanitizedEvaluationItem, value: EvalAnswer) => {
      setAnswers((prev) => ({ ...prev, [item.id]: value }));
      scheduleAutoSave(item.id, value);
    },
    [scheduleAutoSave]
  );

  // Retake — POSTs to the same /attempts endpoint that started the first
  // try. The server returns a fresh in_progress attempt with empty items
  // and an incremented attempt_number; we wipe local state so the UI
  // doesn't show stale answers from the previous attempt.
  const handleRetake = useCallback(async () => {
    if (isRetaking) return;
    setIsRetaking(true);
    try {
      // Cancel any in-flight auto-saves so they don't race with the new attempt.
      debounceTimers.current.forEach((t) => clearTimeout(t));
      debounceTimers.current.clear();
      setQuestionIdx(0);
      setAnswers({});
      setPendingSave(new Set());
      await start.mutateAsync();
    } catch (err) {
      const code = errorCode(err);
      if (code === 'MAX_ATTEMPTS_REACHED') {
        toast.error('You have reached the maximum number of attempts.');
        // The next render will pick this up via `currentAttempt.error`
        // and route to the cap fallback.
      } else {
        toast.error('Could not start a new attempt. Please try again.');
      }
    } finally {
      setIsRetaking(false);
    }
  }, [isRetaking, start]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await finalize.mutateAsync();
    } catch (err) {
      const code = errorCode(err);
      if (code === 'NO_ACTIVE_ATTEMPT') {
        try {
          await start.mutateAsync();
          await finalize.mutateAsync();
        } catch {
          toast.error('Could not submit. Please try again.');
        }
      } else if (code === 'ATTEMPT_ALREADY_FINALIZED') {
        // already submitted — results will show
      } else if (code === 'MAX_ATTEMPTS_REACHED') {
        toast.error('You have reached the maximum number of attempts.');
        navigate(routes.base);
        return;
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [finalize, start, navigate, routes.base]);

  // Course-level percent for the top progress bar (matches CourseModulePage).
  const completedCount = flat.filter(
    (f) =>
      ((f.module as { progress?: { status?: string } }).progress?.status ?? '') === 'completed'
  ).length;
  const coursePercent = flat.length > 0 ? (completedCount / flat.length) * 100 : 0;

  // ── Render branches ──────────────────────────────────────────────────

  if (errorCode(currentAttempt.error) === 'MAX_ATTEMPTS_REACHED') {
    return (
      <motion.div
        {...slideUp}
        className="w-[95%] sm:w-[90%] mx-auto py-6 flex-1 flex flex-col gap-6 min-h-0"
      >
        <LearnerModuleHeader
          backTo={`${routes.base}/sections/${sectionId}/modules`}
          percent={coursePercent}
          sectionTitle={sectionTitle}
          backOptions={[
            {
              label: 'Back to modules',
              to: `${routes.base}/sections/${sectionId}/modules`,
            },
            {
              label: 'Back to sections',
              to: `${routes.base}/sections`,
            },
          ]}
        />
        <div className="flex-1 flex flex-col min-h-0 pb-2">
          <ModuleViewContainer padding="px-4 pb-4">
            <div className="flex-1 flex flex-col items-center justify-center px-3 sm:px-4 pb-3 sm:pb-4 text-center gap-4">
              <p className="text-text-primary font-semibold">Maximum attempts reached.</p>
              <button
                onClick={() => navigate(routes.base)}
                className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-full text-sm font-bold bg-gradient-to-t from-primary-700 to-primary-500 hover:brightness-110"
              >
                Back to Course
              </button>
            </div>
          </ModuleViewContainer>
        </div>
      </motion.div>
    );
  }

  const attemptResponse = currentAttempt.data?.response;
  const isFinished =
    attemptResponse?.status === 'graded' || attemptResponse?.status === 'submitted';

  // Retake gating — show the "Retake quiz" CTA when the learner failed
  // and hasn't hit the configured `max_attempts` cap yet (a null/missing
  // cap means unlimited). Compared inline against the *just-finalized*
  // attempt's `attempt_number`.
  const finalPercentage = attemptResponse?.final_percentage;
  const passedFinal =
    passingGrade != null && finalPercentage != null
      ? finalPercentage >= passingGrade
      : null;
  const attemptNumber =
    (attemptResponse as { attempt_number?: number } | undefined)?.attempt_number ?? 0;
  const maxAttempts =
    (evalModule?.details as { max_attempts?: number | null } | undefined)?.max_attempts ?? null;
  const canRetake =
    passedFinal === false && (maxAttempts == null || attemptNumber < maxAttempts);

  if (isFinished) {
    return (
      <motion.div
        {...slideUp}
        className="w-[95%] sm:w-[90%] mx-auto py-6 flex-1 flex flex-col gap-6 min-h-0"
      >
        <LearnerModuleHeader
          backTo={`${routes.base}/sections/${sectionId}/modules`}
          percent={coursePercent}
          sectionTitle={sectionTitle}
          backOptions={[
            {
              label: 'Back to modules',
              to: `${routes.base}/sections/${sectionId}/modules`,
            },
            {
              label: 'Back to sections',
              to: `${routes.base}/sections`,
            },
          ]}
        />
        <div className="flex-1 flex flex-col min-h-0 pb-2">
          <ModuleViewContainer padding="px-4 pb-4">
            {evalModule?.title && (
              <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
                <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                  {evalModule.title}
                </h2>
              </div>
            )}
            <div className="flex-1 flex flex-col min-h-0 px-3 sm:px-4 pb-3 sm:pb-4">
              <div className="flex-1 min-h-0 rounded-2xl bg-surface-primary shadow-[0_4px_10px_rgba(0,0,0,0.1)] p-4 sm:p-6 overflow-y-auto scrollbar-thin">
                <QuizResultView
                  response={attemptResponse!}
                  passingGradePercent={passingGrade}
                  mode="learner"
                  canRetry={canRetake}
                />
              </div>
            </div>
            <div
              className={`flex items-center gap-4 px-3 sm:px-4 pb-3 sm:pb-4 ${
                canRetake ? 'justify-between' : 'justify-end'
              }`}
            >
              {canRetake && (
                <button
                  type="button"
                  onClick={handleRetake}
                  disabled={isRetaking}
                  aria-busy={isRetaking}
                  className="inline-flex items-center justify-center gap-2 min-w-[151px] h-9 px-4 rounded-full text-sm font-bold shrink-0 active:scale-[0.97] transition-[background-color,transform,opacity] duration-150 disabled:cursor-not-allowed disabled:opacity-60 hover:brightness-110 shadow-[0_2px_8px_rgba(105,72,170,0.25)] bg-gradient-to-t from-primary-700 to-primary-500"
                >
                  <ArrowPathIcon
                    className={`w-4 h-4 ${isRetaking ? 'animate-spin' : ''}`}
                  />
                  {isRetaking ? 'Starting…' : 'Retake quiz'}
                </button>
              )}
              <NextPill
                onClick={goNext}
                label={
                  nextModule
                    ? 'Continue'
                    : attemptResponse?.status === 'submitted'
                      ? 'Back to course'
                      : 'Finish course'
                }
                finish={!nextModule && attemptResponse?.status !== 'submitted'}
              />
            </div>
          </ModuleViewContainer>
        </div>
      </motion.div>
    );
  }

  const isLoading = currentAttempt.isLoading || !evalModule;
  const total = sortedItems.length;
  const safeIdx = Math.max(0, Math.min(questionIdx, Math.max(0, total - 1)));
  const currentItem = sortedItems[safeIdx];
  const isLastQuestion = total === 0 ? true : safeIdx >= total - 1;
  const subPercent = total === 0 ? 0 : ((safeIdx + 1) / total) * 100;

  const handlePrev = () => {
    if (safeIdx > 0) setQuestionIdx(safeIdx - 1);
  };
  const handleNext = () => {
    if (isLastQuestion) {
      handleSubmit();
    } else {
      setQuestionIdx(safeIdx + 1);
    }
  };

  return (
    <motion.div
      {...slideUp}
      className="w-[95%] sm:w-[90%] mx-auto py-6 flex-1 flex flex-col gap-6 min-h-0"
    >
      <LearnerModuleHeader
        backTo={`${routes.base}/sections/${sectionId}/modules`}
        percent={coursePercent}
        sectionTitle={sectionTitle}
        showBack={false}
      />

      <div className="flex-1 flex flex-col min-h-0 pb-2">
        <ModuleViewContainer padding="px-4 pb-4">
          {evalModule?.title && (
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                {evalModule.title}
              </h2>
            </div>
          )}

          {/* Sub-progress bar — Figma 9140:142334. Gradient fill, "Question
              N of M" label centered below. */}
          {!isLoading && total > 0 && (
            <div className="px-5 sm:px-6 pb-4">
              <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800/60 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-[width] duration-300 ease-out"
                  style={{ width: `${subPercent}%` }}
                />
              </div>
              <p className="mt-2 text-center text-sm text-text-secondary">
                Question {safeIdx + 1} of {total}
              </p>
            </div>
          )}

          {/* Question card — Figma 9140:142339. Shows ONE question at a
              time; navigation lives on the Previous/Next pills below.
              Sized to its content (no flex-1) so the white card doesn't
              stretch to fill the gradient and leave empty space inside. */}
          <div className="px-3 sm:px-4 pb-3 sm:pb-4">
            <div className="rounded-2xl bg-surface-primary shadow-[0_4px_10px_rgba(0,0,0,0.1)] p-5 sm:p-7">
              {isLoading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-6 w-1/3 rounded bg-text-tertiary/10" />
                  <div className="h-8 w-2/3 rounded bg-text-tertiary/10" />
                  <div className="h-12 rounded-full bg-text-tertiary/10" />
                  <div className="h-12 rounded-full bg-text-tertiary/10" />
                </div>
              ) : total === 0 ? (
                <p className="text-sm text-text-secondary italic">
                  This evaluation has no questions yet.
                </p>
              ) : currentItem ? (
                <QuestionCard
                  item={currentItem}
                  index={safeIdx}
                  answer={answers[currentItem.id] ?? getDefaultEvalAnswer(currentItem)}
                  onChange={(v) => handleAnswerChange(currentItem, v)}
                  saving={pendingSave.has(currentItem.id)}
                  saved={
                    !pendingSave.has(currentItem.id) &&
                    !!currentAttempt.data?.response?.items?.[currentItem.id]?.submitted_at
                  }
                  disabled={isSubmitting}
                />
              ) : null}
            </div>
          </div>

          {/* Previous + Next at the bottom of the gradient card — Figma
              9140:142339 layout. */}
          {!isLoading && total > 0 && (
            <div className="flex items-center justify-between gap-4 px-3 sm:px-4 pb-3 sm:pb-4">
              <PrevPill onClick={handlePrev} disabled={safeIdx === 0 || isSubmitting} />
              <NextPill
                onClick={handleNext}
                loading={isSubmitting}
                label={isLastQuestion ? 'Submit' : 'Next'}
                finish={isLastQuestion}
              />
            </div>
          )}
        </ModuleViewContainer>
      </div>
    </motion.div>
  );
};

export default EvaluationPlayerPage;

// ── Sub-components ──────────────────────────────────────────────────────

interface QuestionCardProps {
  item: SanitizedEvaluationItem;
  index: number;
  answer: EvalAnswer;
  onChange: (value: EvalAnswer) => void;
  saving: boolean;
  saved: boolean;
  disabled: boolean;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  item,
  index,
  answer,
  onChange,
  saving,
  saved,
  disabled,
}) => {
  const { View } = getEvalViewEntry(item.type);
  const eyebrow = questionTypeLabel(item.type);
  // The question text lives in different fields depending on the type's
  // schema — most types put it in `config.question`. Fall back to top-level
  // `prompt` / `question` for compatibility with older shapes.
  type AnyItem = {
    prompt?: string;
    question?: string;
    config?: { question?: string };
  };
  const a = item as AnyItem;
  const promptText = a.prompt ?? a.question ?? a.config?.question ?? '';

  return (
    <motion.div {...fadeIn} className="space-y-5">
      <div className="flex items-start gap-3 sm:gap-4">
        <span className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary-500 text-sm font-bold flex items-center justify-center shadow-[0_2px_8px_rgba(105,72,170,0.25)]">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-bold text-text-tertiary uppercase tracking-[0.18em]">
              {eyebrow}
            </p>
            {saving && (
              <span className="text-[11px] text-primary-500 animate-pulse">Saving…</span>
            )}
            {!saving && saved && (
              <span className="text-[11px] text-success-600">Saved</span>
            )}
          </div>
          {promptText && (
            <h3 className="mt-1 text-lg sm:text-xl font-bold text-text-primary leading-snug">
              {promptText}
            </h3>
          )}
        </div>
      </div>

      <View
        item={item}
        mode="learner"
        value={answer}
        onChange={onChange}
        disabled={disabled}
      />
    </motion.div>
  );
};
